import { prepare, prepareWithSegments, layout, layoutWithLines, type PreparedText, type PreparedTextWithSegments } from '@chenglou/pretext'

export interface TextMetrics {
  width: number
  height: number
  lineCount: number
}

export interface FontStyles {
  font: string
  lineHeight: number
  fontSize: number
  fontFamily: string
  fontWeight: string
}

export function getFontFromStyles(styles: CSSStyleDeclaration): string {
  return styles.font.length > 0
    ? styles.font
    : `${styles.fontStyle} ${styles.fontVariant} ${styles.fontWeight} ${styles.fontSize} / ${styles.lineHeight} ${styles.fontFamily}`
}

export function parseLineHeight(styles: CSSStyleDeclaration): number {
  const lineHeight = styles.lineHeight
  if (lineHeight === 'normal') return parseFloat(styles.fontSize) * 1.2
  return parseFloat(lineHeight)
}

export function parsePx(value: string): number {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export interface PreparedItem {
  id: string
  text: string
  prepared: PreparedText
}

export class PretextCache {
  private cache = new Map<string, PreparedText>()
  private font: string = ''

  refresh(font: string, items: Array<{ id: string; text: string }>): PreparedItem[] {
    if (this.font === font) {
      return items.map(item => ({
        id: item.id,
        text: item.text,
        prepared: this.cache.get(item.id) || prepare(item.text, font)
      }))
    }

    this.font = font
    this.cache.clear()

    return items.map(item => {
      const prepared = prepare(item.text, font)
      this.cache.set(item.id, prepared)
      return { id: item.id, text: item.text, prepared }
    })
  }

  get(id: string): PreparedText | undefined {
    return this.cache.get(id)
  }

  set(id: string, prepared: PreparedText): void {
    this.cache.set(id, prepared)
  }

  clear(): void {
    this.cache.clear()
    this.font = ''
  }
}

export function measureTextHeight(
  text: string,
  font: string,
  maxWidth: number,
  lineHeight: number
): TextMetrics {
  const prepared = prepare(text, font)
  const { lineCount } = layout(prepared, maxWidth, lineHeight)
  return {
    width: maxWidth,
    height: lineCount * lineHeight,
    lineCount
  }
}

export function measurePreparedText(
  prepared: PreparedText,
  maxWidth: number,
  lineHeight: number
): TextMetrics {
  const { lineCount } = layout(prepared, maxWidth, lineHeight)
  return {
    width: maxWidth,
    height: lineCount * lineHeight,
    lineCount
  }
}

export interface LayoutLine {
  text: string
  width: number
}

export function getTextLines(
  text: string,
  font: string,
  maxWidth: number,
  lineHeight: number
): LayoutLine[] {
  const prepared = prepareWithSegments(text, font)
  const { lines } = layoutWithLines(prepared, maxWidth, lineHeight)
  return lines.map(line => ({
    text: line.text,
    width: line.width
  }))
}

export function truncateToLines(
  text: string,
  font: string,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
  ellipsis: string = '...'
): { text: string; isTruncated: boolean; fullHeight: number } {
  const prepared = prepareWithSegments(text, font)
  const { lines, lineCount } = layoutWithLines(prepared, maxWidth, lineHeight)
  const fullHeight = lineCount * lineHeight

  if (lines.length <= maxLines) {
    return { text, isTruncated: false, fullHeight }
  }

  const visibleLines = lines.slice(0, maxLines)
  const lastLine = visibleLines[visibleLines.length - 1]

  // Truncate last line and add ellipsis
  let truncatedLastLine = lastLine.text
  const ellipsisWidth = measureTextWidth(ellipsis, font)
  const availableWidth = maxWidth - ellipsisWidth

  // Binary search for the right truncation point
  let left = 0
  let right = truncatedLastLine.length
  while (left < right) {
    const mid = Math.ceil((left + right) / 2)
    const testText = truncatedLastLine.slice(0, mid)
    const testWidth = measureTextWidth(testText, font)
    if (testWidth <= availableWidth) {
      left = mid
    } else {
      right = mid - 1
    }
  }

  truncatedLastLine = truncatedLastLine.slice(0, left) + ellipsis
  visibleLines[visibleLines.length - 1] = {
    text: truncatedLastLine,
    width: measureTextWidth(truncatedLastLine, font)
  }

  const truncatedText = visibleLines.map(l => l.text).join(' ')
  return { text: truncatedText, isTruncated: true, fullHeight }
}

export function measureTextWidth(text: string, font: string): number {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  ctx.font = font
  return ctx.measureText(text).width
}

// Debounced resize handler
export function onResize(callback: () => void, delay: number = 100): () => void {
  let timeout: number | null = null

  const handler = () => {
    if (timeout) window.clearTimeout(timeout)
    timeout = window.setTimeout(callback, delay)
  }

  window.addEventListener('resize', handler)

  return () => {
    window.removeEventListener('resize', handler)
    if (timeout) window.clearTimeout(timeout)
  }
}
